import { useState } from 'react';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { processQueue, cancelJob, retryJob } from './queueController';
import { downloadAllAsZip, downloadSingle } from './zipDownload';
import { SectionCard } from '@/components/SectionCard';
import { Button } from '@/components/Button';
import { ProgressBar } from '@/components/ProgressBar';
import { Select } from '@/components/Select';
import type { RenderJobStatus } from '@/types';

const STATUS_LABELS: Record<RenderJobStatus, string> = {
  draft: 'Draft',
  queued: 'Queued',
  'preparing-voiceover': 'Preparing voiceover',
  rendering: 'Rendering',
  complete: 'Complete',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const STATUS_COLORS: Record<RenderJobStatus, string> = {
  draft: 'text-slate-400',
  queued: 'text-slate-500',
  'preparing-voiceover': 'text-amber-500',
  rendering: 'text-brand-600',
  complete: 'text-green-600',
  failed: 'text-red-600',
  cancelled: 'text-slate-400',
};

export function BulkQueuePanel() {
  const workspace = useWorkspaceStore((s) => s.workspace);
  const enqueueJob = useWorkspaceStore((s) => s.enqueueJob);
  const removeJob = useWorkspaceStore((s) => s.removeJob);
  const reorderJobs = useWorkspaceStore((s) => s.reorderJobs);
  const [concurrency, setConcurrency] = useState(1);
  const [selectedProjectId, setSelectedProjectId] = useState('');

  const jobs = workspace.jobOrder.map((id) => workspace.jobs[id]).filter(Boolean);
  const projectOptions = workspace.projectOrder
    .map((id) => workspace.projects[id])
    .filter(Boolean)
    .map((p) => ({ value: p.id, label: p.title }));

  function addToQueue(projectId: string) {
    enqueueJob(projectId);
  }

  function addAllToQueue() {
    for (const id of workspace.projectOrder) addToQueue(id);
  }

  function startProcessing() {
    void processQueue(concurrency);
  }

  async function handleDownloadAll() {
    await downloadAllAsZip(jobs);
  }

  function moveJob(index: number, direction: -1 | 1) {
    const order = [...workspace.jobOrder];
    const target = index + direction;
    if (target < 0 || target >= order.length) return;
    [order[index], order[target]] = [order[target], order[index]];
    reorderJobs(order);
  }

  return (
    <div className="space-y-4">
      <SectionCard title="Add to bulk queue">
        <div className="flex gap-2">
          <div className="flex-1">
            <Select
              label="Reel project"
              value={selectedProjectId || projectOptions[0]?.value || ''}
              options={projectOptions.length ? projectOptions : [{ value: '', label: 'No Reels yet' }]}
              onChange={setSelectedProjectId}
            />
          </div>
          <div className="self-end">
            <Button onClick={() => addToQueue(selectedProjectId || projectOptions[0]?.value)} disabled={projectOptions.length === 0}>
              Add
            </Button>
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={addAllToQueue} disabled={projectOptions.length === 0}>
          Add all Reels
        </Button>
      </SectionCard>

      <SectionCard title="Queue">
        <div className="flex items-center gap-2">
          <div className="w-40">
            <Select
              label="Concurrent renders"
              value={String(concurrency)}
              options={[
                { value: '1', label: '1 (safest)' },
                { value: '2', label: '2 (faster, more RAM/CPU)' },
              ]}
              onChange={(v) => setConcurrency(Number(v))}
            />
          </div>
          <div className="self-end flex gap-2">
            <Button variant="primary" onClick={startProcessing} disabled={jobs.every((j) => j.status !== 'queued')}>
              Generate all
            </Button>
            <Button onClick={handleDownloadAll} disabled={!jobs.some((j) => j.status === 'complete')}>
              Download all (ZIP)
            </Button>
          </div>
        </div>

        {jobs.length === 0 ? (
          <p className="text-xs text-slate-400">No jobs queued yet. Add a Reel above to get started.</p>
        ) : (
          <ul className="space-y-2">
            {jobs.map((job, index) => {
              const project = workspace.projects[job.projectId];
              return (
                <li key={job.id} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{project?.title ?? 'Deleted Reel'}</p>
                      <p className={`text-xs font-medium ${STATUS_COLORS[job.status]}`}>{STATUS_LABELS[job.status]}</p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button onClick={() => moveJob(index, -1)} className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Move up">
                        ↑
                      </button>
                      <button onClick={() => moveJob(index, 1)} className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Move down">
                        ↓
                      </button>
                    </div>
                  </div>

                  {(job.status === 'rendering' || job.status === 'preparing-voiceover') && <ProgressBar value={job.progress} />}
                  {job.status === 'failed' && job.error && <p className="mt-1 text-xs text-red-600">{job.error}</p>}

                  <div className="mt-2 flex flex-wrap gap-2">
                    {job.status === 'rendering' && (
                      <Button size="sm" variant="danger" onClick={() => cancelJob(job.id)}>
                        Cancel
                      </Button>
                    )}
                    {(job.status === 'failed' || job.status === 'cancelled') && (
                      <Button size="sm" onClick={() => retryJob(job.id)}>
                        Retry
                      </Button>
                    )}
                    {job.status === 'complete' && (
                      <Button size="sm" variant="secondary" onClick={() => downloadSingle(job)}>
                        Download ({job.resultFileName})
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => removeJob(job.id)}>
                      Remove from queue
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}
